import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Button, Flex, HStack, Text, VStack } from '@bonusly/design-system';

import { useBizyDreamingPreferences } from '../../queries/bizy-dreaming-preferences';
import { useGithubAuthorize, useGithubDisconnect, useGithubStatus } from '../../queries/github';
import { GithubLogo } from './github-logo';
import { GithubScopePicker } from './github-scope-picker';

interface GithubConnectCardProps {
	onManageRepositories?: () => void;
	returnPath?: string;
}

export const GithubConnectCard: FC<GithubConnectCardProps> = ({ onManageRepositories, returnPath }) => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'github' });
	const { data: status, isLoading: isLoadingStatus } = useGithubStatus();
	const { mutateAsync: authorize, isLoading: isAuthorizing } = useGithubAuthorize();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useGithubDisconnect();
	const { data: dreamingPreferences } = useBizyDreamingPreferences();
	const { track } = useAnalytics();

	const connectionStatus = status?.status;
	const githubDreaming = dreamingPreferences?.dreamingPreferences.github;

	useEffect(() => {
		if (connectionStatus === 'connected' && sessionStorage.getItem('bizy_connecting_github')) {
			track(AnalyticsEvent.Github.Connected);
			sessionStorage.removeItem('bizy_connecting_github');
		}
	}, [connectionStatus, track]);

	const handleConnect = useCallback(async () => {
		track(AnalyticsEvent.Github.ConnectClicked);
		try {
			const { redirectUrl } = await authorize(returnPath ?? window.location.pathname);
			if (redirectUrl) {
				sessionStorage.setItem('bizy_connecting_github', 'true');
				window.location.href = redirectUrl;
			}
		} catch {
			// Error handled by React Query
		}
	}, [authorize, returnPath, track]);

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Github.Disconnected);
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, track]);

	const sharedSummary = useMemo(() => {
		const repoCount = status?.selectedRepos?.length ?? 0;
		const orgCount = status?.selectedOrgs?.length ?? 0;

		if (repoCount === 0 && orgCount === 0) return null;
		if (orgCount === 0) return t('sharedSummary.reposOnly', { count: repoCount });
		if (repoCount === 0) return t('sharedSummary.orgsOnly', { count: orgCount });
		return t('sharedSummary.reposAndOrgs', { repoCount, orgCount, count: orgCount });
	}, [status?.selectedOrgs, status?.selectedRepos, t]);

	if (isLoadingStatus) return null;

	const isConnected = connectionStatus === 'connected';
	const isExpired = connectionStatus === 'expired';
	// The company connector can back this member's dreaming even when they
	// haven't linked a personal token, so it also unlocks the dedicated manage
	// page (where the dreaming toggle and optional personal-connect live).
	const isCompanyConfigured = !!githubDreaming?.companyConfigured;
	const useDedicatedManagement = (isConnected || isCompanyConfigured) && !!onManageRepositories;
	const statusLabel = isConnected
		? t('status.connected')
		: isExpired
			? t('status.reconnectRequired')
			: isCompanyConfigured
				? t('status.companyConnected')
				: t('status.notConnected');
	const statusColor = isExpired ? 'text.warning' : 'text.softer';

	return (
		<Box py={3} borderBottom="1px solid" borderColor="border.soft">
			<Flex alignItems="center" justifyContent="space-between">
				<HStack spacing={3} align="center">
					<Box color="text.default" flexShrink={0} display="inline-flex" alignItems="center">
						<GithubLogo width="24" height="24" />
					</Box>
					<VStack spacing={0} align="flex-start">
						<Text size="md" variant="strong" color="text.default">
							{t('name')}
						</Text>
						<Text size="sm" color={statusColor}>
							{useDedicatedManagement && sharedSummary ? sharedSummary : statusLabel}
						</Text>
					</VStack>
				</HStack>
				{useDedicatedManagement ? (
					<Button size="md" colorScheme="primary" onClick={onManageRepositories} variant="outline">
						{t('manageRepositories')}
					</Button>
				) : isConnected ? (
					<Button size="md" variant="ghost" colorScheme="secondary" onClick={handleDisconnect} isLoading={isDisconnecting}>
						{t('disconnect')}
					</Button>
				) : (
					<Button size="md" colorScheme="primary" onClick={handleConnect} isLoading={isAuthorizing}>
						{isExpired ? t('reconnect') : t('connect')}
					</Button>
				)}
			</Flex>
			{isConnected && !useDedicatedManagement && <GithubScopePicker selectedOrgs={status?.selectedOrgs ?? []} selectedRepos={status?.selectedRepos ?? []} />}
		</Box>
	);
};
